import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInvalidateAnalysisRuns } from '@/lib/analyses/useAnalysisRuns';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { alignmentsAtom } from '@/atoms/alignment.atom';
import { clearancePreviewAtom } from '@/atoms/clearance.atom';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { ClearanceProfileEditor, useShippedProfileNames } from '@/components/ClearanceProfileEditor';
import { AnalysisDialogShell } from '@/components/analyses/AnalysisDialogShell';
import {
  AdvancedSection,
  AnalysisSection,
  InputRoleField,
  OutputSummary,
  UnloadedAlignmentsOffer,
  type InputRoleGroup,
} from '@/components/analyses/sections';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericField } from '@/components/ui/numeric-field';
import type { Alignment } from '@/core/alignment';
import { execute } from '@/graphql/execute';
import { useFeature } from '@/hooks/useFeature';
import { useUnloadedAlignmentLayers } from '@/hooks/useUnloadedAlignmentLayers';
import {
  clearanceDefaults,
  clearancePayload,
  clearanceRangeLabel,
  deriveClearance,
  INTERVAL_PRESETS,
  MAX_OUTPUT_NAME_CHARS,
  type ClearanceFormValues,
} from '@/lib/clearance/dialogModel';
import { useDialog } from '@/providers/DialogProvider';
import { startClearanceMutation } from '@/queries/clearance.query';
import { isClearanceCandidate } from '@/lib/analyses/candidates';

export interface RunClearanceLayer {
  id: string;
  name: string;
  type?: string | null;
}

// Moved to `@/lib/analyses/candidates`; re-exported for existing importers.
export { isClearanceCandidate };

export interface RunClearanceDialogProps {
  projectId: string;
  sourceLayer: RunClearanceLayer;
  candidateLayers: RunClearanceLayer[];
}

export const RunClearanceDialog = ({
  projectId,
  sourceLayer,
  candidateLayers,
}: RunClearanceDialogProps) => {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const formId = useId();
  const queryClient = useQueryClient();
  const alignments = useAtomValue(alignmentsAtom);
  const allLayers = useAtomValue(layersAtom);
  const setPreview = useSetAtom(clearancePreviewAtom);
  const setTrackers = useSetAtom(deviationTrackingAtom);
  const gate = useFeature('analysis.clearance');
  // LandXML layers whose alignments were never parsed: switched off in the
  // viewer, or — opened from the Data Center — every one of them, since no
  // layer is mounted there. Reading one is a click (see the hook).
  const { candidates: unloadedAlignmentLayers, load: loadAlignmentsFor } = useUnloadedAlignmentLayers();

  // Plain useState rather than react-hook-form: this form's validity is one
  // derived value (`deriveClearance`) over a handful of fields, and the
  // profile editor is already values-in/patches-out. A resolver here would be
  // a second source of truth for the same rules.
  const [values, setValues] = useState<ClearanceFormValues>(() => ({
    ...clearanceDefaults(),
    pointcloudLayerId: sourceLayer.id,
  }));
  const patch = (p: Partial<ClearanceFormValues>) => setValues((v) => ({ ...v, ...p }));

  // Finding N1: the same set the editor's Save guard uses, so Run cannot arm
  // on a name Save would have refused.
  const reservedProfileNames = useShippedProfileNames();
  const derived = useMemo(
    () => deriveClearance(values, alignments, reservedProfileNames),
    [values, alignments, reservedProfileNames],
  );

  /** Alignments grouped by their owning layer's name — the station-volumes
   *  picker's shape. A layer that has since been deleted groups under a
   *  fallback label rather than vanishing. */
  const groupedAlignments = useMemo(() => {
    const byLayer = new Map<string, Alignment[]>();
    for (const a of [...alignments.values()].sort((x, y) => x.name.localeCompare(y.name))) {
      const layer = allLayers.find((l) => l.id === a.layerId);
      const label = layer?.name ?? t('clearance.dialog.alignmentOrphanGroup');
      const list = byLayer.get(label);
      if (list) list.push(a);
      else byLayer.set(label, [a]);
    }
    return byLayer;
  }, [alignments, allLayers, t]);
  const alignmentGroups: InputRoleGroup[] = [...groupedAlignments.entries()].map(([layerName, group]) => ({
    key: layerName,
    label: layerName,
    layers: group.map((a) => ({ id: a.key, name: a.name })),
  }));

  // Auto-select when the project has exactly one alignment: the station
  // volumes dialog does this, and a one-item picker the user must still open
  // is friction with no information in it.
  useEffect(() => {
    if (values.alignmentKey) return;
    const only = [...alignments.values()];
    if (only.length === 1) setValues((v) => ({ ...v, alignmentKey: only[0].key }));
  }, [alignments, values.alignmentKey]);

  // Publish the 3D preview while the dialog is open; clear it on unmount so a
  // cancelled dialog never leaves a ribbon in the scene.
  useEffect(() => {
    if (!derived.alignment || derived.profileError !== null) {
      setPreview(null);
      return;
    }
    setPreview({
      key: derived.alignment.key,
      vertices: values.profileVertices,
      verticalOffsetM: values.verticalOffsetM,
      from: values.stationFromText.trim() ? derived.fromInternal : null,
      to: values.stationToText.trim() ? derived.toInternal : null,
    });
  }, [derived, values, setPreview]);
  useEffect(() => () => setPreview(null), [setPreview]);

  const invalidateAnalysisRuns = useInvalidateAnalysisRuns();
  const mutation = useMutation({
    mutationFn: () => execute(startClearanceMutation, { input: { projectId, ...clearancePayload(values, derived) } }),
    onSuccess: (data) => {
      invalidateAnalysisRuns(projectId);
      const batchId = data.startClearance;
      // The picker can point `pointcloudLayerId` at a different cloud than
      // `sourceLayer` (the one the dialog was opened from), so the toast
      // names the cloud that was actually run, not the entry point.
      const runLayerName = candidateLayers.find((l) => l.id === values.pointcloudLayerId)?.name ?? sourceLayer.name;
      // Same lifecycle as RunCompareSurfacesDialog
      // (CLAUDE.md's "persistent loading toast" convention): a `toast.loading`
      // that `useDeviationProgress` resolves to success/failure, instead of a
      // one-shot "started" toast the user has no way to know the outcome of.
      const toastId = toast.loading(t('clearance.dialog.runningToast', { name: runLayerName }), {
        duration: Infinity,
      });
      // Snapshot layers already derived from this pointcloud. The tracker
      // resolves on the first NEW layer with `metadata.derivedFrom` pointing
      // here — postprocess sets that from `clearanceCtx.pointcloudLayerId`
      // (server/background/src/functions/postprocess.ts), the exact same
      // shape a deviation run's output layer carries.
      const existingDerivedLayerIds = allLayers
        .filter((l) => (l.metadata as { derivedFrom?: string } | null)?.derivedFrom === values.pointcloudLayerId)
        .map((l) => l.id);
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: values.pointcloudLayerId,
          sourceLayerName: runLayerName,
          existingDerivedLayerIds,
          kind: 'clearance' as const,
        },
      ]);
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      dismissActiveDialog();
    },
    // No swallowed errors: the resolver's message is the actionable one
    // (which cap was exceeded, which vertex pair crossed).
    onError: (err: Error) => toast.error(err.message),
  });

  const rangeLabel = clearanceRangeLabel(derived);
  // CLAUDE.md's feature-gating rule: a gated button must stay CLICKABLE so the
  // click can open the upgrade dialog. The HTML `disabled` attribute swallows
  // the click, which would make the `promptUpgrade` branch below unreachable
  // dead code. Only the form's own blocking reasons disable the button; the
  // subscription gate is expressed as a muted style plus the handler.
  const blocked = derived.blocking !== null || mutation.isPending;
  const locked = !gate.allowed;
  // The station range lives in the collapsed Advanced section; a range the
  // derivation rejects opens it, so the reason under Run has a field on screen.
  const rangeTouched = values.stationFromText.trim() !== '' || values.stationToText.trim() !== '';
  const rangeHasError =
    derived.blocking === 'rangeInvalid' ||
    (rangeTouched && (derived.blocking === 'rangeTooShort' || derived.blocking === 'tooManyBins'));

  return (
    <AnalysisDialogShell
      title={t('clearance.dialog.title')}
      description={t('clearance.dialog.description')}
      formId={formId}
      primaryLabel={locked ? t('clearance.dialog.runLocked') : t('clearance.dialog.run')}
      // Only the form's own blocking reasons disable Run; the subscription
      // gate is the muted, still-clickable locked state (CLAUDE.md).
      primaryDisabled={blocked}
      primaryBusy={mutation.isPending}
      primaryLocked={locked}
      onPrimaryLockedClick={gate.promptUpgrade}
      primaryTestId='clearance-run'>
      <form
        id={formId}
        className='flex min-w-0 flex-col gap-4 px-1 py-2'
        onSubmit={(e) => {
          e.preventDefault();
          if (blocked) return;
          // The gate is re-read here, not trusted from render: a
          // subscription can lapse while the dialog is open and this
          // mutation costs real compute.
          if (!gate.allowed) {
            gate.promptUpgrade();
            return;
          }
          mutation.mutate();
        }}
        // Before the shell there was no <form>, so Enter in a field did
        // nothing. Keep it that way (plan D-P3-4): a stray Enter in a profile
        // cell must not start a paid run.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target instanceof HTMLInputElement) e.preventDefault();
        }}>
        <AnalysisSection title={t('analysisConfig.sections.inputs')} testId='clearance-section-inputs'>
          <InputRoleField
            role='asBuilt'
            id='clearance-cloud'
            testId='clearance-cloud-picker'
            layers={candidateLayers.filter(isClearanceCandidate)}
            value={values.pointcloudLayerId}
            onChange={(id) => patch({ pointcloudLayerId: id })}
          />
          {/* The station-volumes alignment picker's shape (spec §3:
              "alignment picker (station-volumes pattern)"): items GROUPED BY
              OWNING LAYER, so a project with three LandXML files does not
              present forty flat names. A required input, not an add-on. */}
          <InputRoleField
            role='alignment'
            id='clearance-alignment'
            testId='clearance-alignment-picker'
            layers={[]}
            groups={alignmentGroups}
            value={values.alignmentKey}
            onChange={(key) => patch({ alignmentKey: key })}
            placeholder={t('clearance.dialog.alignmentPlaceholder')}
            // With a LandXML to load below, "Register an alignment first"
            // would send the user to do what they already did.
            emptyMessage={
              unloadedAlignmentLayers.length > 0
                ? t('clearance.dialog.alignmentsNotLoaded')
                : t('stationVolumes.dialog.noAlignments')
            }
          />
          <UnloadedAlignmentsOffer
            layers={unloadedAlignmentLayers}
            onLoad={(id) => void loadAlignmentsFor(id)}
            testIdPrefix='clearance'
          />
        </AnalysisSection>

        <AnalysisSection title={t('analysisConfig.sections.settings')} testId='clearance-section-settings'>
          <ClearanceProfileEditor
            value={{
              profileId: values.profileId,
              profileName: values.profileName,
              sourceAttribution: values.profileSourceAttribution,
              readOnly: values.profileReadOnly,
              vertices: values.profileVertices,
            }}
            verticalOffsetM={values.verticalOffsetM}
            onChange={(p) =>
              patch({
                ...(p.profileId !== undefined ? { profileId: p.profileId } : {}),
                ...(p.profileName !== undefined ? { profileName: p.profileName } : {}),
                ...(p.sourceAttribution !== undefined ? { profileSourceAttribution: p.sourceAttribution } : {}),
                ...(p.readOnly !== undefined ? { profileReadOnly: p.readOnly } : {}),
                ...(p.vertices !== undefined ? { profileVertices: p.vertices } : {}),
              })
            }
          />

          {/* ONE shared grid, not label+input stacks. Labels get their own
              bottom-aligned row, so a label that wraps to two lines ("Vertical
              offset (m)") can no longer push its input out of line with its
              neighbour's. The interval shortcuts get a real grid row of their
              own and wrap — as a nested `flex` they once overflowed the column
              and slid under the neighbouring cell, whose transparent area then
              ate every click on the "50" preset. */}
          <div className='grid grid-cols-2 gap-x-3 gap-y-1.5'>
            <Label className='self-end' htmlFor='clearance-vertical-offset'>
              {t('clearance.dialog.verticalOffset')}
            </Label>
            <Label className='self-end' htmlFor='clearance-interval'>
              {t('clearance.dialog.interval')}
            </Label>
            {/* NumericField, not <Input type="number">: a −2 m datum is the
                normal case here, and parseFloat('-') in an onChange handler
                makes a leading minus impossible to type. */}
            <NumericField
              id='clearance-vertical-offset'
              data-testid='clearance-vertical-offset'
              value={values.verticalOffsetM}
              onChange={(v) => patch({ verticalOffsetM: v })}
              step='0.01'
            />
            <NumericField
              id='clearance-interval'
              data-testid='clearance-interval'
              value={values.stationInterval}
              onChange={(v) => patch({ stationInterval: v })}
              step='1'
              min='1'
              max='500'
            />
            <div className='col-start-2 flex flex-wrap gap-1'>
              {INTERVAL_PRESETS.map((p) => (
                <button
                  key={p}
                  type='button'
                  data-testid={`clearance-interval-${p}`}
                  className='rounded border border-slate-200 px-1.5 text-[11px] hover:bg-slate-50 dark:border-stone-700 dark:hover:bg-stone-800'
                  onClick={() => patch({ stationInterval: p })}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </AnalysisSection>

        <AnalysisSection title={t('analysisConfig.sections.output')} testId='clearance-section-output'>
          <OutputSummary
            testId='clearance-output'
            creates={[
              t('analysisConfig.creates.clearanceCloud', {
                name: values.name.trim() || '…',
                interpolation: { escapeValue: false },
              }),
              t('analysisConfig.creates.clearanceCsv'),
            ]}
            warnings={[derived.estimate.warnBins && !derived.blocking && t('clearance.dialog.warnManyBins')]}>
            <p data-testid='clearance-estimate' className='text-xs text-slate-500 dark:text-stone-400'>
              {/* `count` picks the plural, `formatted` is what is printed —
                  grouped, so 20 000 intervals is not read as "20000". */}
              {t('clearance.dialog.estimate', {
                count: derived.estimate.binCount,
                formatted: derived.estimate.binCount.toLocaleString(),
                range: rangeLabel ?? '—',
              })}
            </p>
            <div className='flex min-w-0 flex-col gap-1.5'>
              <Label htmlFor='clearance-name'>{t('analysisConfig.resultName')}</Label>
              <Input
                id='clearance-name'
                data-testid='clearance-name'
                value={values.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder={t('clearance.dialog.namePlaceholder')}
                // Finding F-5: this was the one unbounded field on the 8 KB ECS
                // payload path. `maxLength` stops the paste before it can arm
                // the Run button; `deriveClearance`'s `outputNameTooLong` is the
                // authoritative check this mirrors.
                maxLength={MAX_OUTPUT_NAME_CHARS}
              />
            </div>
          </OutputSummary>
        </AnalysisSection>

        <AdvancedSection testId='clearance-advanced' hasError={rangeHasError}>
          <div className='grid grid-cols-2 gap-x-3 gap-y-1.5'>
            <Label className='self-end' htmlFor='clearance-station-from'>
              {t('clearance.dialog.stationFrom')}
            </Label>
            <Label className='self-end' htmlFor='clearance-station-to'>
              {t('clearance.dialog.stationTo')}
            </Label>
            <Input
              id='clearance-station-from'
              data-testid='clearance-station-from'
              value={values.stationFromText}
              placeholder={t('clearance.dialog.stationPlaceholder')}
              onChange={(e) => patch({ stationFromText: e.target.value })}
            />
            <Input
              id='clearance-station-to'
              data-testid='clearance-station-to'
              value={values.stationToText}
              placeholder={t('clearance.dialog.stationPlaceholder')}
              onChange={(e) => patch({ stationToText: e.target.value })}
            />
          </div>
        </AdvancedSection>

        {derived.blocking && (
          <p data-testid='clearance-blocking' className='text-xs text-red-600 dark:text-red-400'>
            {/* Finding I10: this used to reprint `derived.profileError` — the
                same sentence the editor already shows in place, in red, right
                above. Opening the dialog on an empty profile therefore lit two
                identical errors before the user had touched anything. The
                editor owns the WHY (next to the field it is about); this line
                owns only the "and that is what is holding Run back". */}
            {derived.blocking === 'nameReserved'
              ? // Deliberately the SAME string the editor's Save refusal shows
                // (`clearance.profile.nameReserved`) rather than a second copy
                // under `dialog.blocking` — one rule, one sentence, one key.
                t('clearance.profile.nameReserved')
              : t(`clearance.dialog.blocking.${derived.blocking}`)}
          </p>
        )}
      </form>
    </AnalysisDialogShell>
  );
};
